import { useContext } from 'react';

// modules
import { FormattedMessage } from 'react-intl';

// contexts
import { PlayerContext } from '../../../contexts/PlayerContext';
import { AppContext } from '../../../contexts/AppContext';

// mui
import { useTheme } from '@mui/material/styles';
import { Box, Link, Stack, Typography } from '@mui/material';
import { PlayerAudio } from '../../../interfaces/player/player.interface';

// handlers
import { formatDurationPunctuated } from '../../player/odiaPlayerHandlers';

// utils
import { formatDate } from '../../../utils/formatDate';

// interfaces
import { MobileOrientation } from '../../../interfaces/playlist/mobile.interface';


interface AudioDescriptorProps {
    audio: PlayerAudio,
    visualStyle: AudioDescriptorVisualStyle,
    view?: string,
    mobileViewOrientation?: MobileOrientation,
    isPlayedAudio?: boolean
}
interface AudioDescriptorVisualStyle {
    highlightSelectedAudio: boolean,
    imageSize: string,
    showDate: boolean,
    showDescription: boolean,
    showDuration: boolean,
    showImage: boolean,
    showPublisher: boolean,
    showTitle: boolean,
    showTopic: boolean,
    truncateDescription: boolean,
    truncateTitle: boolean,
}


export const AudioDescriptor = ({
    audio, visualStyle, view, mobileViewOrientation = 'portrait', isPlayedAudio = false
}: AudioDescriptorProps) => {
    const theme = useTheme();
    const { palette } = theme;

    const { appLang } = useContext(AppContext);
    const { lang } = appLang;
    const { current: currentContextLanguage } = lang;

    const playerContext = useContext(PlayerContext);
    const { type, style } = playerContext;
    const { visual } = style;

    const {
        primaryColor,
        secondaryColor,
    } = visual;

    const {
        publisherTitle,
        publisherDescription,
        publisherWebsiteUrl,
        publisherLabel,
        imageUrl,
        publisherPublicationDate,
        topicId,
        duration,
        selected
    } = audio;

    const {
        highlightSelectedAudio,
        imageSize,
        showDate,
        showDescription,
        showDuration,
        showImage,
        showPublisher,
        showTitle,
        showTopic,
        truncateDescription,
        truncateTitle,
    } = visualStyle;


    let truncateDescriptionCssId = truncateDescription ? "truncateLongTexts" : "";

    const applyAudioSelected = isPlayedAudio ? true : selected;

    return (
        <Stack
            direction='row'
            sx={
                mobileViewOrientation === 'portrait'
                    ? {
                        display: 'flex',
                        alignItems: 'top',
                        pt: '5px',
                        pl: type.design === 'large' ? '8px' : '20px'
                    }
                    : {
                        display: 'flex',
                        alignItems: 'top',
                        pt: 2,
                        pl: 0.2
                    }
            }
        >
            {
                showImage &&
                imageUrl &&
                imageUrl !== '' &&
                view !== 'portal' &&
                < Box
                    display="flex"
                    alignItems={type.design === "large" ? "top" : "center"}
                    justifyContent="center"
                    pr={mobileViewOrientation === 'portrait' ? 2 : 1}
                >
                    <img
                        id="article-image"
                        src={imageUrl}
                        alt={publisherTitle}
                        style={{
                            border:
                                selected && highlightSelectedAudio
                                    ? `2px solid ${primaryColor}`
                                    : `2px solid transparent`,
                            borderRadius: 6,
                            height: imageSize,
                            objectFit: 'cover',
                            width: imageSize,
                        }}
                    />
                </Box>
            }
            <Stack direction='column'>
                {
                    showTitle &&
                    truncateTitle &&
                    view !== 'portal' &&
                    <div id="largePlayerTitle">
                        <Typography
                            aria-label='label title'
                            variant={type.design === "large" ? "caption" : "subtitle2"}
                            color={(applyAudioSelected === true && palette.mode !== 'dark') ? primaryColor : palette.text.secondary}
                        >
                            <b>{publisherTitle}</b>
                        </Typography>
                    </div>

                }
                {
                    showTitle &&
                    !truncateTitle &&
                    view !== 'portal' &&
                    <Typography
                        aria-label='label title'
                        color={selected ? primaryColor : secondaryColor}
                        variant={type.design === "large" ? "caption" : "subtitle2"}
                    >
                        <b>{publisherTitle}</b>
                    </Typography>
                }
                {
                    showDescription &&
                    publisherDescription?.length > 0 &&
                    view !== 'portal' &&
                    <div id={truncateDescriptionCssId}>
                        <Typography
                            aria-label='playlistAudioDescription'
                            color={secondaryColor}
                            variant="caption"
                        >
                            {publisherDescription}
                        </Typography>
                    </div>
                }
                {
                    view !== 'portal' &&
                    (
                        showPublisher ||
                        showDate ||
                        showTopic ||
                        showDuration
                    )
                    &&
                    <Stack direction='row'>
                        <div id="largePlayerDescription">
                            {
                                (
                                    showPublisher ||
                                    showDate
                                ) &&
                                <Typography
                                    aria-label='playlistAudioPublisherDate'
                                    color={secondaryColor}
                                    variant="caption"
                                    mr={2}
                                >
                                    {
                                        showPublisher && type.design !== "large" && mobileViewOrientation !== 'landscape' &&
                                        <>
                                            <FormattedMessage id='app.playlist-by-label' />&nbsp;
                                        </>
                                    }
                                    {
                                        showPublisher &&
                                        <>
                                            {
                                                (publisherWebsiteUrl && publisherWebsiteUrl !== '') ?
                                                    <Link href={publisherWebsiteUrl} color={secondaryColor}><b>{publisherLabel}</b></Link>
                                                    : <b>{publisherLabel}&nbsp;</b>
                                            }
                                        </>
                                    }
                                    {
                                        showDate && type.design !== "large" &&
                                        <>
                                            &nbsp;
                                            <FormattedMessage id='app.playlist-at-label' />
                                        </>
                                    }
                                    {
                                        showDate &&
                                        <b>&nbsp;{formatDate(publisherPublicationDate, currentContextLanguage)}</b>
                                    }
                                </Typography>
                            }
                            {
                                showTopic &&
                                topicId > -1 &&
                                <Typography
                                    aria-label='playlistAudioTopic'
                                    color={secondaryColor}
                                    variant="caption"
                                    mr={2}
                                >
                                    <b>
                                        <FormattedMessage id={`app.speech-topics-id-${topicId}`} />
                                    </b>
                                </Typography>
                            }
                            {
                                showDuration &&
                                duration > 0 &&
                                <Typography
                                    aria-label='playlistAudioDuration'
                                    color={secondaryColor}
                                    variant="caption"
                                >
                                    <b>{formatDurationPunctuated(duration)}</b>
                                </Typography>
                            }
                        </div>
                    </Stack>
                }
            </Stack>
        </Stack >
    )
}